import React from 'react';
import { Box, Typography, Container, Grid } from '@mui/material';
import AnimatedIcon from './AnimatedIcon';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import BuildIcon from '@mui/icons-material/Build';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ExploreIcon from '@mui/icons-material/Explore';
import PsychologyIcon from '@mui/icons-material/Psychology';
import SchoolIcon from '@mui/icons-material/School';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import Logo from './Logo';
import { keyframes } from '@emotion/react';
import { useNavigate } from 'react-router-dom';

// Animated gradient background keyframes
const animatedBg = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Add animated SVG waves for the hero background
const HeroWaves = () => (
  <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none', overflow: 'hidden' }}>
    <svg width="100%" height="100%" viewBox="0 0 1440 320" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ position: 'absolute', top: 0, left: 0 }}>
      <defs>
        <linearGradient id="waveGradient" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FE6B8B" />
          <stop offset="100%" stopColor="#6e7eea" />
        </linearGradient>
      </defs>
      <path fill="url(#waveGradient)" fillOpacity="0.18">
        <animate attributeName="d" dur="8s" repeatCount="indefinite"
          values="M0,160L80,170C160,180,320,200,480,192C640,184,800,144,960,133.3C1120,123,1280,149,1360,162.7L1440,176L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z;
                  M0,180L80,160C160,140,320,120,480,128C640,136,800,176,960,186.7C1120,197,1280,171,1360,157.3L1440,144L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z;
                  M0,160L80,170C160,180,320,200,480,192C640,184,800,144,960,133.3C1120,123,1280,149,1360,162.7L1440,176L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z" />
      </path>
    </svg>
  </Box>
);

const SolutionPage = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{
      height: '100vh',
      width: '100vw',
      overflowY: 'auto',
      scrollSnapType: 'y mandatory',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      backgroundSize: '200% 200%',
      animation: `${animatedBg} 12s ease-in-out infinite`,
      position: 'relative',
    }}>
      {/* Slide 1: Our Solution Hero */}
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 2, scrollSnapAlign: 'start', position: 'relative', overflow: 'hidden' }}>
        <Typography variant="h2" sx={{ color: '#fff', fontWeight: 900, mb: 2, fontSize: { xs: '2.8rem', md: '4.5rem' }, letterSpacing: 1, position: 'relative', zIndex: 1 }}>
          Our Solution
        </Typography>
        <Typography variant="h4" sx={{ color: 'rgba(255,255,255,0.97)', maxWidth: 900, mb: 4, fontWeight: 700, fontSize: { xs: 26, md: 36 }, zIndex: 1 }}>
          Pathly is more than a planner—it's your AI-powered guide to building a high school journey that is authentic, strategic, and uniquely you.
        </Typography>
        <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.90)', maxWidth: 700, fontWeight: 400, fontSize: { xs: 20, md: 26 }, zIndex: 1 }}>
          We combine smart technology with proven guidance to help you discover your passions, craft a standout profile, and focus on what truly matters for college and beyond.
        </Typography>
      </Box>
      {/* Slide 2: How Pathly Works - Stepper with aligned line */}
      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', px: 2, scrollSnapAlign: 'start', position: 'relative' }}>
        <Typography variant="h3" sx={{ color: '#fff', fontWeight: 900, mb: 6, fontSize: { xs: '2rem', md: '2.8rem' }, letterSpacing: 1, textShadow: '0 4px 24px #6e7eea88' }}>
          How Pathly Works
        </Typography>
        <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', zIndex: 1, mt: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 1 }}>
            {/* Step 1 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 260, height: 260, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 4, boxShadow: '0 4px 24px 0 rgba(110,126,234,0.10)', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 8px 32px 0 rgba(110,126,234,0.18)', transform: 'scale(1.04)', bgcolor: 'rgba(110,126,234,0.10)' }, py: 4, mx: 1, justifyContent: 'center' }}>
              <AnimatedIcon icon={<PsychologyIcon fontSize="large" />} label="AI Discovery" highlight />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mt: 2, fontSize: 22 }}>AI Discovery</Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.93)', mt: 1, fontSize: 18, textAlign: 'center' }}>
                Our AI learns your interests, strengths, and goals through smart prompts and analysis.
              </Typography>
            </Box>
            {/* Step 2 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 260, height: 260, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 4, boxShadow: '0 4px 24px 0 rgba(110,126,234,0.10)', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 8px 32px 0 rgba(110,126,234,0.18)', transform: 'scale(1.04)', bgcolor: 'rgba(110,126,234,0.10)' }, py: 4, mx: 1, justifyContent: 'center' }}>
              <AnimatedIcon icon={<ExploreIcon fontSize="large" />} label="Personalized Path" highlight />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mt: 2, fontSize: 22 }}>Personalized Path</Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.93)', mt: 1, fontSize: 18, textAlign: 'center' }}>
                Get tailored recommendations for extracurriculars, projects, and leadership that fit you not just what looks good.
              </Typography>
            </Box>
            {/* Step 3 */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 260, height: 260, bgcolor: 'rgba(255,255,255,0.08)', borderRadius: 4, boxShadow: '0 4px 24px 0 rgba(110,126,234,0.10)', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 8px 32px 0 rgba(110,126,234,0.18)', transform: 'scale(1.04)', bgcolor: 'rgba(110,126,234,0.10)' }, py: 4, mx: 1, justifyContent: 'center' }}>
              <AnimatedIcon icon={<BuildIcon fontSize="large" />} label="Profile Builder" highlight />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700, mt: 2, fontSize: 22 }}>Profile Builder</Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.93)', mt: 1, fontSize: 18, textAlign: 'center' }}>
                Craft a compelling, authentic story for your college applications no more generic resumes.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Slide 3: Why Pathly Works - Clean, centered grid */}
      <Box sx={{ height: '100vh', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column', alignItems: 'stretch', textAlign: 'center', px: 2, pt: 0, scrollSnapAlign: 'start', position: 'relative', boxSizing: 'border-box', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto', flex: 'none', mt: 0, pt: 0 }}>
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 4, mt: 12, textAlign: 'center' }}>
            Why Pathly Works
          </Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <Box sx={{ width: '100%', maxWidth: 1100, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'center', alignItems: 'center', gap: { xs: 3, md: 4 } }}>
            {/* Card 1 */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 4, p: 4, boxShadow: '0 6px 32px 0 rgba(110,126,234,0.18)', height: 260, minHeight: 260, maxHeight: 260, width: 290, maxWidth: 290, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 12px 48px 0 rgba(110,126,234,0.28)', transform: 'scale(1.045)' }, justifyContent: 'center', overflow: 'visible' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative', height: 54, zIndex: 1 }}>
                <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 0 }}>
                  <Box sx={{ width: 54, height: 54, borderRadius: '50%', background: 'linear-gradient(120deg, #FE6B8B33 0%, #6e7eea33 100%)', filter: 'blur(2px)' }} />
                </Box>
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 54 }}>
                  <AnimatedIcon icon={<AutoAwesomeIcon fontSize="large" />} label="Personalized Brilliance" />
                </Box>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, fontSize: 20, background: 'linear-gradient(90deg, #FE6B8B 0%, #6e7eea 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', zIndex: 1 }}>
                Personalized Brilliance
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', mt: 1, textAlign: 'center', fontSize: 16, zIndex: 1 }}>
                Every student’s journey is unique. Pathly’s AI crafts a plan that’s as individual as you are.
              </Typography>
            </Box>
            {/* Card 2 */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 4, p: 4, boxShadow: '0 6px 32px 0 rgba(110,126,234,0.18)', height: 260, minHeight: 260, maxHeight: 260, width: 290, maxWidth: 290, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 12px 48px 0 rgba(110,126,234,0.28)', transform: 'scale(1.045)' }, justifyContent: 'center', overflow: 'visible' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative', height: 54, zIndex: 1 }}>
                <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 0 }}>
                  <Box sx={{ width: 54, height: 54, borderRadius: '50%', background: 'linear-gradient(120deg, #FE6B8B33 0%, #6e7eea33 100%)', filter: 'blur(2px)' }} />
                </Box>
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 54 }}>
                  <AnimatedIcon icon={<RocketLaunchIcon fontSize="large" />} label="Accelerated Growth" />
                </Box>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, fontSize: 20, background: 'linear-gradient(90deg, #FE6B8B 0%, #6e7eea 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', zIndex: 1 }}>
                Accelerated Growth
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', mt: 1, textAlign: 'center', fontSize: 16, zIndex: 1 }}>
                Focus on what matters most, skip the guesswork, and make real progress toward your goals.
              </Typography>
            </Box>
            {/* Card 3 */}
            <Box sx={{ bgcolor: '#fff', borderRadius: 4, p: 4, boxShadow: '0 6px 32px 0 rgba(110,126,234,0.18)', height: 260, minHeight: 260, maxHeight: 260, width: 290, maxWidth: 290, display: 'flex', flexDirection: 'column', alignItems: 'center', transition: 'box-shadow 0.2s, transform 0.2s', '&:hover': { boxShadow: '0 12px 48px 0 rgba(110,126,234,0.28)', transform: 'scale(1.045)' }, justifyContent: 'center', overflow: 'visible' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 1, position: 'relative', height: 54, zIndex: 1 }}>
                <Box sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', zIndex: 0 }}>
                  <Box sx={{ width: 54, height: 54, borderRadius: '50%', background: 'linear-gradient(120deg, #FE6B8B33 0%, #6e7eea33 100%)', filter: 'blur(2px)' }} />
                </Box>
                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 54 }}>
                  <AnimatedIcon icon={<VerifiedUserIcon fontSize="large" />} label="Confidence for College" />
                </Box>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mt: 1, fontSize: 20, background: 'linear-gradient(90deg, #FE6B8B 0%, #6e7eea 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', zIndex: 1 }}>
                Confidence for College
              </Typography>
              <Typography variant="body1" sx={{ color: '#333', mt: 1, textAlign: 'center', fontSize: 16, zIndex: 1 }}>
                Show colleges your authentic self—with a story that’s both impressive and true.
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ mt: 4, mb: 2, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', flex: 0 }}>
          <Box
            component="button"
            onClick={() => navigate('/demo')}
            sx={{
              display: 'inline-block',
              px: 6,
              py: 2,
              fontWeight: 700,
              fontSize: 20,
              color: '#fff',
              background: 'linear-gradient(90deg, #FE6B8B 0%, #6e7eea 100%)',
              borderRadius: 99,
              boxShadow: '0 4px 24px #6e7eea55',
              textDecoration: 'none',
              letterSpacing: 1,
              border: 'none',
              cursor: 'pointer',
              transition: 'background 0.2s, box-shadow 0.2s, transform 0.2s',
              '&:hover': {
                background: 'linear-gradient(90deg, #6e7eea 0%, #FE6B8B 100%)',
                boxShadow: '0 8px 32px #FE6B8B55',
                transform: 'scale(1.06)'
              }
            }}
          >
            See Pathly in Action
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SolutionPage; 